/**
 * handoffOfferRouting — pure decision for routing an inbound WebRTC `offer`.
 *
 * PHASE 9 (duplicate/generic modal): a Customer SDP offer for an AI-Voice handoff is routed to the
 * operator's BASE firebase uid, so it fans out to ALL of that operator's signaling sessions. For the
 * session that ALREADY ACCEPTED the handoff (armed as answerer, with a media room whose call_id/room_id
 * matches the offer), the offer must be CONSUMED by that accepted media session — answered in place,
 * NOT rung as a second generic incoming-call modal. Every other offer falls through to the normal
 * staff↔staff ring path (unchanged), which still shows its IncomingCallModal.
 *
 * Behavior-identical to the inline guard in useWebRTC.handleWebSocketMessage; extracted so the
 * "one active-call interface, no duplicate modal" contract is unit-testable.
 */

// TRUE → answer this offer on the already-accepted handoff media session (no ring / no modal).
// FALSE → normal ring path (IncomingCallModal), preserving direct staff↔staff calls.
export function shouldAnswerAsMediaOffer(message, { answererArmed, mediaRoom } = {}) {
  if (!answererArmed || !mediaRoom || !message) return false;
  const cid = message.call_id;
  if (cid === undefined || cid === null) return false;
  return cid === mediaRoom.call_id || cid === mediaRoom.room_id;
}

/**
 * TRUE when an inbound `offer` belongs to a CANONICAL OPERATOR-CALL session (a scheduled encounter or
 * an AI-Voice handoff), as stated by the SERVER on the signalling envelope.
 *
 * The backend tags every operator-call offer with `source` (operator_call_sessions.source:
 * "encounter" | "ai_voice"); a staff-to-staff call has no such row and therefore no source. Android and
 * iOS Business both discriminate on exactly this field.
 *
 * IMPORTANT: this only IDENTIFIES the offer. It must never, on its own, suppress the generic ring — a
 * previous build did exactly that and produced a SILENT ring when the canonical path failed to claim the
 * session, which is strictly worse than the defect it was fixing. Suppression requires a positive claim
 * (see `shouldSuppressGenericRing`).
 */
export function isOperatorCallOffer(message) {
  const src = message && message.source;
  return typeof src === 'string' && src.trim().length > 0;
}

/**
 * TRUE only when the canonical operator-call ring has POSITIVELY CLAIMED this exact session, so the
 * generic staff ring may stand down for it.
 *
 * The generic ring is the safety net. It is removed for a session only once something else is
 * demonstrably ringing for that same session — never merely because the offer looked classified.
 * SILENT_RING_FAILURE_MODE=IMPOSSIBLE follows from this being the only suppression path.
 */
export function shouldSuppressGenericRing(message, claimedSessionIds) {
  if (!isOperatorCallOffer(message)) return false;
  const cid = message && message.call_id;
  if (!cid) return false;
  if (!claimedSessionIds) return false;
  return typeof claimedSessionIds.has === 'function' ? claimedSessionIds.has(cid) : false;
}
